import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export function ImageUpload({ value, onChange, label = "Image", testId = "image-upload", quiet = false }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      if (!quiet) toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const src = value?.startsWith("/api") ? fileUrl(value) : value;

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <div className="flex items-center gap-4">
        <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          {src ? (
            <>
              <img src={src} alt="preview" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange("")}
                className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5 text-slate-500 hover:text-rose-500"
                data-testid={`${testId}-remove`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <ImagePlus className="h-6 w-6 text-slate-300" />
          )}
        </div>
        <div>
          <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={`${testId}-input`} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-60"
            data-testid={`${testId}-btn`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {value ? "Change" : "Upload"}
          </button>
          <p className="mt-1 text-xs text-slate-400">JPG, PNG, WEBP or GIF · max 5MB</p>
        </div>
      </div>
    </div>
  );
}
